/**
 * useChat Hook
 *
 * Main hook for managing chat state, messages, and streaming API interactions.
 * Uses Server-Sent Events (SSE) for real-time token-by-token responses.
 * Handles optimistic UI updates, loading states, error handling,
 * and clarification responses for query understanding.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type {
  Message,
  ClarificationResponse,
  ClarificationOption,
  StreamingState,
  Source,
} from '../types';
import { useLocalStorage } from './useLocalStorage';
import {
  fetchChatHistory,
  sendChatMessageStreaming,
  isStreamingAvailable,
} from '../lib/api';
import { generateUUID } from '../lib/utils';
import CONFIG from '../config';

interface UseChatReturn {
  messages: Message[];
  isLoading: boolean;
  isLoadingHistory: boolean;
  error: string | null;
  sessionId: string;
  clarification: ClarificationResponse | null;
  /** Streaming state - contains partial content during streaming */
  streamingState: StreamingState;
  sendMessage: (
    content: string,
    getAuthHeader: () => Record<string, string>,
  ) => Promise<string>;
  clearConversation: () => void;
  selectClarificationOption: (
    option: ClarificationOption,
    getAuthHeader: () => Record<string, string>,
  ) => Promise<void>;
  /** Dismiss clarification prompt without action - user can rephrase their query */
  dismissClarification: () => void;
  /** Cancel an in-progress streaming request */
  cancelStreaming: () => void;
}

interface UseChatOptions {
  chatId?: string;
  getAuthHeader?: () => Record<string, string>;
  defaultClient?: string;
}

export function useChat(options: UseChatOptions = {}): UseChatReturn {
  const { chatId, getAuthHeader, defaultClient } = options;

  // Session ID - persisted in localStorage (used only when no chatId provided)
  const [sessionId, setSessionId] = useLocalStorage<string>(
    'chat_session_id',
    generateUUID(),
  );

  // Use chatId from URL if provided, otherwise use sessionId
  const effectiveSessionId = chatId || sessionId;

  // Messages - NOT persisted (fresh on reload)
  const [messages, setMessages] = useState<Message[]>([]);

  // Loading state for sending messages
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Loading state for fetching history
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);

  // Track if we've attempted to fetch history for current chatId
  const [historyFetchedFor, setHistoryFetchedFor] = useState<string | null>(
    null,
  );

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Clarification state - holds pending clarification from query understanding
  const [clarification, setClarification] =
    useState<ClarificationResponse | null>(null);

  // Streaming state - tracks partial content during streaming responses
  const [streamingState, setStreamingState] = useState<StreamingState>({
    isStreaming: false,
    content: '',
    sources: null,
    messageId: null,
  });

  // Pending query for retry after clarification (ref because it's only used in callbacks)
  const pendingQueryRef = useRef<string | null>(null);

  // Request sequence counter to prevent race conditions
  const requestSequenceRef = useRef<number>(0);

  // Abort controller for streaming requests - also used to detect if a request is in progress
  const streamAbortControllerRef = useRef<AbortController | null>(null);

  // Fetch conversation history when chatId is provided
  useEffect(() => {
    // Only fetch if we have a chatId and auth, and haven't fetched for this chatId yet
    if (!chatId || !getAuthHeader || historyFetchedFor === chatId) {
      return;
    }

    const abortController = new AbortController();

    const loadHistory = async () => {
      setIsLoadingHistory(true);
      setError(null);

      try {
        const authHeader = getAuthHeader();
        const response = await fetchChatHistory(
          chatId,
          authHeader,
          abortController.signal,
        );

        if (response.messages && response.messages.length > 0) {
          setMessages(response.messages);

          if (CONFIG.DEBUG) {
            console.log(
              `Loaded ${response.messages.length} messages for session ${chatId.substring(0, 8)}...`,
            );
          }
        }
      } catch (err) {
        // Ignore abort errors - these are intentional
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        // Don't show error for empty history (404 or no messages)
        if (CONFIG.DEBUG) {
          console.log('No history found or error loading:', err);
        }
      } finally {
        // Only update state if not aborted
        if (!abortController.signal.aborted) {
          setIsLoadingHistory(false);
          setHistoryFetchedFor(chatId);
        }
      }
    };

    loadHistory();

    // Cleanup: abort the request if chatId changes or component unmounts
    return () => {
      abortController.abort();
    };
  }, [chatId, getAuthHeader, historyFetchedFor]);

  // Track previous chatId to detect navigation between chats
  // Using React's recommended "state update during render" pattern
  // See: https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevChatId, setPrevChatId] = useState<string | undefined>(chatId);
  if (prevChatId !== chatId) {
    // chatId changed - clear state for fresh start (runs synchronously during render)
    setPrevChatId(chatId);
    setMessages([]);
    setError(null);
    setClarification(null);
    pendingQueryRef.current = null;
    // Reset history fetch tracker so navigating back to a chat will re-fetch
    setHistoryFetchedFor(null);
  }

  /**
   * Send a streaming message to the chat API
   * Uses SSE to receive tokens incrementally for real-time responses.
   *
   * @param content - The message content to send
   * @param getAuthHeader - Function to get authorization header
   * @param client - Optional client filter for retrieval
   * @param skipOptimisticUpdate - If true, don't add user message (for clarification retries)
   */
  const sendMessageStreaming = useCallback(
    async (
      content: string,
      getAuthHeader: () => Record<string, string>,
      client?: string,
      skipOptimisticUpdate = false,
    ): Promise<string> => {
      // Prevent double-sends by checking if abort controller exists (request in progress)
      if (streamAbortControllerRef.current) {
        return effectiveSessionId;
      }

      // Increment sequence to invalidate any in-flight requests
      const currentSequence = ++requestSequenceRef.current;

      // Create new abort controller for this request
      const abortController = new AbortController();
      streamAbortControllerRef.current = abortController;

      // Clear any previous errors and clarification
      setError(null);
      setClarification(null);
      pendingQueryRef.current = null;

      // Optimistic UI update - add user message immediately (unless skipped for retry)
      if (!skipOptimisticUpdate) {
        const userMessage: Message = {
          role: 'user',
          content: content.trim(),
          timestamp: Date.now() / 1000,
        };
        setMessages((prev) => [...prev, userMessage]);
      }

      // Set loading and streaming state
      setIsLoading(true);
      setStreamingState({
        isStreaming: true,
        content: '',
        sources: null,
        messageId: null,
      });

      try {
        const authHeader = getAuthHeader();
        let accumulatedContent = '';
        let sources: Source[] | null = null;
        let messageId: string | null = null;

        // Consume the async generator
        for await (const event of sendChatMessageStreaming(
          content.trim(),
          authHeader,
          CONFIG.MAX_RESULTS,
          client,
          effectiveSessionId, // Pass session ID for conversation history
          abortController.signal,
        )) {
          // Check if this request is still valid
          if (currentSequence !== requestSequenceRef.current) {
            return effectiveSessionId;
          }

          switch (event.type) {
            case 'token':
              accumulatedContent += event.content;
              setStreamingState((prev) => ({
                ...prev,
                content: accumulatedContent,
              }));
              break;

            case 'sources':
              sources = event.sources;
              setStreamingState((prev) => ({
                ...prev,
                sources,
              }));
              break;

            case 'clarification':
              // Handle clarification response from streaming endpoint
              // Store the query for retry after user selects an option
              pendingQueryRef.current = content.trim();
              setClarification(event.clarification);
              setStreamingState({
                isStreaming: false,
                content: '',
                sources: null,
                messageId: null,
              });

              if (CONFIG.DEBUG) {
                console.log(
                  'Clarification needed from streaming:',
                  event.clarification.clarification_type,
                );
              }
              return effectiveSessionId;

            case 'done': {
              messageId = event.message_id;
              // Finalize: add complete message to messages array
              const assistantMessage: Message = {
                role: 'assistant',
                content: accumulatedContent,
                timestamp: Date.now() / 1000,
                sources: sources || undefined,
              };
              setMessages((prev) => [...prev, assistantMessage]);
              setStreamingState({
                isStreaming: false,
                content: '',
                sources: null,
                messageId: null,
              });

              if (CONFIG.DEBUG) {
                console.log('Streaming complete:', {
                  messageId,
                  contentLength: accumulatedContent.length,
                  sourcesCount: sources?.length || 0,
                });
              }
              break;
            }

            case 'error':
              throw new Error(event.message || 'Stream error');
          }
        }

        return effectiveSessionId;
      } catch (err) {
        // Check if this request is still valid
        if (currentSequence !== requestSequenceRef.current) {
          return effectiveSessionId;
        }

        // If we have partial content, save it
        const partialContent = streamingState.content;
        if (partialContent) {
          const partialMessage: Message = {
            role: 'assistant',
            content: partialContent + '\n\n[Response interrupted]',
            timestamp: Date.now() / 1000,
          };
          setMessages((prev) => [...prev, partialMessage]);
        }

        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to send message. Please try again.';

        setError(errorMessage);

        if (CONFIG.DEBUG) {
          console.error('Streaming error:', err);
        }

        // Reset streaming state
        setStreamingState({
          isStreaming: false,
          content: '',
          sources: null,
          messageId: null,
        });

        return effectiveSessionId;
      } finally {
        if (currentSequence === requestSequenceRef.current) {
          setIsLoading(false);
          streamAbortControllerRef.current = null;
        }
      }
    },
    [effectiveSessionId, streamingState.content],
  );

  /**
   * Cancel an in-progress streaming request
   */
  const cancelStreaming = useCallback(() => {
    if (streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort();
      streamAbortControllerRef.current = null;

      if (CONFIG.DEBUG) {
        console.log('Streaming cancelled by user');
      }
    }
  }, []);

  /**
   * Send a message to the chat API using streaming
   * Implements optimistic UI updates for instant feedback
   * Returns the chat ID used for this message (useful for navigation on first message)
   */
  const sendMessage = useCallback(
    async (
      content: string,
      getAuthHeader: () => Record<string, string>,
    ): Promise<string> => {
      // Don't send empty messages or if already sending (check abort controller)
      if (!content.trim() || streamAbortControllerRef.current) {
        return effectiveSessionId;
      }

      // Check if streaming endpoint is configured
      if (!isStreamingAvailable()) {
        setError(
          'Chat service not configured. Please set VITE_REST_API_ENDPOINT.',
        );
        return effectiveSessionId;
      }

      return sendMessageStreaming(content, getAuthHeader, defaultClient);
    },
    [effectiveSessionId, sendMessageStreaming, defaultClient],
  );

  /**
   * Handle user selecting a clarification option
   * Retries the original query with the selected filter applied using streaming
   */
  const selectClarificationOption = useCallback(
    async (
      option: ClarificationOption,
      getAuthHeader: () => Record<string, string>,
    ): Promise<void> => {
      // Use ref to get current pending query (avoids stale closure)
      const currentPendingQuery = pendingQueryRef.current;
      if (!currentPendingQuery || !clarification) {
        return;
      }

      // option.id contains the display_name which matches S3 metadata
      // entity_type is always 'client' (we only support client filtering)
      const client = option.id;

      if (CONFIG.DEBUG) {
        console.log('Selected clarification option:', {
          option: option.display_name,
          client,
        });
      }

      // Retry the query with the selected client filter (skip adding user message again)
      await sendMessageStreaming(
        currentPendingQuery,
        getAuthHeader,
        client,
        true, // skipOptimisticUpdate - user message already shown
      );
    },
    [clarification, sendMessageStreaming],
  );

  /**
   * Dismiss clarification prompt without taking action
   * User can then rephrase their query to include a client name
   */
  const dismissClarification = useCallback((): void => {
    if (CONFIG.DEBUG) {
      console.log('❌ Dismissing clarification prompt');
    }

    // Clear clarification and pending query - user can start fresh
    setClarification(null);
    pendingQueryRef.current = null;
  }, []);

  /**
   * Clear the conversation and start a new session
   */
  const clearConversation = useCallback(() => {
    // Generate new session ID
    const newSessionId = generateUUID();
    setSessionId(newSessionId);

    // Clear messages
    setMessages([]);

    // Clear error
    setError(null);

    // Clear clarification state
    setClarification(null);
    pendingQueryRef.current = null;

    // Reset history fetch tracker so a new chatId will trigger fetch
    setHistoryFetchedFor(null);

    if (CONFIG.DEBUG) {
      console.log(
        'Conversation cleared, new session:',
        newSessionId.substring(0, 8) + '...',
      );
    }
  }, [setSessionId]);

  return {
    messages,
    isLoading,
    isLoadingHistory,
    error,
    sessionId: effectiveSessionId,
    clarification,
    streamingState,
    sendMessage,
    clearConversation,
    selectClarificationOption,
    dismissClarification,
    cancelStreaming,
  };
}
